import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api";

// Initial state
const initialState = {
  users: [],
  selectedUser: null, // Add this for storing single user data
  loading: false,
  error: null,
  pagination: null,
};

// Async thunk to fetch residences
export const fetchResidences = createAsyncThunk(
  "user/fetchResidences",
  async () => {
    const response = await api.get("/houses");
    return response.data;
  }
);

// Async thunk to fetch users
export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (
    { page = 1, pageSize = 4, paginate = false } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = { page, per_page: pageSize };
      if (paginate) params.paginate = true; // only when requested

      const response = await api.get("/users", { params });
      return response?.data; // whole {status, message, data}
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Async thunk to fetch user by ID
export const fetchUserById = createAsyncThunk(
  "user/fetchUserById",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data.data.data || response.data; // Adjust based on your API response structure
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch user");
    }
  }
);

// Async thunk to add a new user
export const addUser = createAsyncThunk(
  "user/addUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post("/users", userData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add user");
    }
  }
);

// Async thunk to edit user details
export const editUser = createAsyncThunk(
  "user/editUser",
  async ({ userId, userData }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/users/${userId}`, userData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to edit user");
    }
  }
);

// Async thunk to delete a user
export const deleteUser = createAsyncThunk(
  "user/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/users/${userId}`);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete user");
    }
  }
);

// Create the user slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch residences
      .addCase(fetchResidences.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchResidences.fulfilled, (state, action) => {
        state.loading = false;
        state.residences = action.payload;
      })
      .addCase(fetchResidences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;

        // Try multiple possible response structures
        let usersArray = [];

        // Check different possible structures
        if (action.payload?.data?.data) {
          usersArray = action.payload.data.data;
        } else if (action.payload?.data) {
          usersArray = action.payload.data;
        } else if (Array.isArray(action.payload)) {
          usersArray = action.payload;
        }

        console.log("✅ Extracted users array:", usersArray);

        // Safely check if paginate was requested
        const paginateRequested = action.meta.arg?.paginate ?? false;

        if (paginateRequested) {
          /* ---- PAGINATED ---- */
          const meta = action.payload?.data?.meta ?? {};

          state.users = usersArray;
          state.pagination = {
            current_page: meta.current_page ?? 1,
            total: meta.total ?? 0,
            per_page: meta.per_page ?? 10,
            last_page: meta.last_page ?? 1,
            from: meta.from ?? null,
            to: meta.to ?? null,
          };
        } else {
          /* ---- NON-PAGINATED ---- */
          state.users = usersArray;
          state.pagination = null;
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Fetch user by ID
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
        state.error = null;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Add user
      .addCase(addUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload);
        state.error = null;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Edit user
      .addCase(editUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editUser.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.findIndex(
          (user) => user.id === action.payload.id
        );
        if (index !== -1) {
          state.users[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(editUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((user) => user.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      });
  },
});

// Export actions and reducer
export const { clearSelectedUser } = userSlice.actions;
export const userReducer = userSlice.reducer;
